import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { systemAdminService } from '../services/admin/SystemAdminService';
import { userService } from '../services/database/userService';
import { Database, Download, Upload, AlertTriangle, CheckCircle, HardDrive, RefreshCw, Users, GraduationCap, BookOpen, Shield, TrendingUp } from 'lucide-react';

const DatabaseAdminPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [userStats, setUserStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [databaseStats, systemStats] = await Promise.all([
        systemAdminService.getDatabaseStats(),
        userService.getSystemStats(),
      ]);
      setStats(databaseStats);
      setUserStats(systemStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleDownloadBackup = async () => {
    setIsLoading(true);
    try {
      await systemAdminService.downloadBackup();
      showMessage('success', 'Backup downloaded successfully');
    } catch (error) {
      console.error('Error downloading backup:', error);
      showMessage('error', 'Failed to download backup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreDatabase = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!confirm('DANGER: Are you sure you want to restore this database? This will OVERWRITE the current server database. This action cannot be undone.')) {
      return;
    }

    setIsLoading(true);
    try {
      await systemAdminService.restoreDatabase(file);
      await loadStats();
      showMessage('success', 'Database restored successfully');
    } catch (error) {
      console.error('Error restoring database:', error);
      showMessage('error', 'Failed to restore database');
    } finally {
      setIsLoading(false);
      // Reset input
      event.target.value = '';
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Database className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">System Database Admin</h1>
            <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
              Server-Side
            </span>
          </div>
          <p className="text-gray-600">Manage the Backend SQLite Database (vatli365.db). Use with caution.</p>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
              message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                'bg-blue-50 text-blue-800 border border-blue-200'
            }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
              message.type === 'error' ? <AlertTriangle className="w-5 h-5" /> :
                <Database className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* User Stats */}
        {userStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {[
              { label: 'Tổng người dùng', value: userStats.totalUsers, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Học sinh', value: userStats.totalStudents, icon: GraduationCap, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Giáo viên', value: userStats.totalTeachers, icon: BookOpen, color: 'text-orange-600', bg: 'bg-orange-50' },
              { label: 'Admin', value: userStats.totalAdmins, icon: Shield, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Chờ duyệt', value: userStats.pendingApprovals, icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
              { label: 'Hoạt động hôm nay', value: userStats.activeToday, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
            ].map(card => (
              <div key={card.label} className={`${card.bg} rounded-xl p-4 border border-white shadow-sm`}>
                <card.icon className={`w-6 h-6 ${card.color} mb-2`} />
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                <p className="text-xs text-gray-600 mt-1">{card.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Database Statistics */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <HardDrive className="w-5 h-5" />
              <span>Database Metadata</span>
            </h2>
            <button onClick={loadStats} className="p-2 text-gray-500 hover:text-blue-600">
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {stats ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-900 mb-2">File Size</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.size_mb} MB</p>
                <p className="text-sm text-blue-600">{stats.size_bytes.toLocaleString()} bytes</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Last Modified</h3>
                <p className="text-lg font-semibold text-gray-700">
                  {new Date(stats.last_modified * 1000).toLocaleString()}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading server stats...</p>
            </div>
          )}
        </div>

        {/* Database Operations */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Database className="w-5 h-5" />
            <span>Operations</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Backup */}
            <div className="border rounded-lg p-4 bg-green-50/50 border-green-100">
              <h3 className="font-medium text-green-900 mb-2 flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Backup Database
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Download a complete copy of the server database (.db file).
              </p>
              <button
                onClick={handleDownloadBackup}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download Backup</span>
              </button>
            </div>

            {/* Restore */}
            <div className="border rounded-lg p-4 bg-red-50/50 border-red-100">
              <h3 className="font-medium text-red-900 mb-2 flex items-center">
                <Upload className="w-4 h-4 mr-2" />
                Restore Database
              </h3>
              <p className="text-sm text-red-600 mb-4 font-medium">
                ⚠️ WARNING: This will overwrite the current server database!
              </p>
              <button
                onClick={() => document.getElementById('restore-db')?.click()}
                disabled={isLoading}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Upload & Restore</span>
              </button>
              <input
                id="restore-db"
                type="file"
                accept=".db,.sqlite"
                onChange={handleRestoreDatabase}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">System Information</h2>
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>Database Path:</strong> ./vatli365.db</p>
            <p><strong>Backend:</strong> Python FastAPI + SQLAlchemy + SQLite (Async)</p>
            <p><strong>Note:</strong> Regular backups are recommended before performing any major system updates.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseAdminPage;